import { useEffect, useState } from 'react';
import { Button, ButtonGroup, Form, Spinner, Table, ToggleButton } from "react-bootstrap";
import toast from 'react-hot-toast';
import { FaCheck, FaEdit, FaSearch, FaTrash, FaUpload } from 'react-icons/fa';
import { IoCloseSharp, IoReturnUpBackOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { Link, Links } from 'react-router-dom';
import AppPagination from '../../Components/AppPagination';
import DeleteOrderModal from '../../Components/Modals/DeleteOrderModal';
import EditOrderModal from '../../Components/Modals/EditOrderModal';
import RejectOrderModal from '../../Components/Modals/RejectOrderModal';
import { addCSV, deleteOrder, getOrders, updateOrder } from '../../Services/orderService';
import { IoMdAdd } from "react-icons/io";

const TrashedOrders = () => {

    const STATUS_CONFIG = {
        confirmed: {
            color: "#10b981",
            bg: "rgba(16,185,129,0.12)",
            label: "Confirmed",
            icon: "✓",
        },
        rejected: {
            color: "#ef4444",
            bg: "rgba(239,68,68,0.12)",
            label: "Rejected",
            icon: "✕",
        },
        deleted: {
            color: "#ef4444",
            bg: "rgba(239,68,68,0.12)",
            label: "Deleted",
            icon: "🗑 ️",
        },
        pending: {
            color: "#f59e0b",
            bg: "rgba(245,158,11,0.12)",
            label: "Pending",
            icon: "⏳",
        },
    };

    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        is_confirmed: 'all',
        from: '',
        to: '',
        trackingId: ''
    });

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [remark, setRemark] = useState("");

    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [action, setAction] = useState("");


    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const companyPin = useSelector(state => state.auth.companyPin);
    const companyId = useSelector(state => state.auth.companyId);

    const base64Pin = btoa(companyPin);


    const formatDateForAPI = (date, isEnd = false) => {
        if (!date) return "";   // undefined nahi, empty string

        const d = new Date(date);

        if (isEnd) {
            d.setHours(23, 59, 59, 999);
        } else {
            d.setHours(0, 0, 0, 0);
        }

        return d.toISOString();
    };

    const fetchOrders = async () => {
        try {
            setLoading(true);

            const res = await getOrders({
                page,
                limit: 50,   // ✅ frontend se hard-coded limit
                is_confirmed: filters.is_confirmed === "all"
                    ? ""        // empty string means fetch all
                    : filters.is_confirmed,
                from: formatDateForAPI(filters.from),
                to: formatDateForAPI(filters.to, true),
                trackingId: filters.trackingId || "",
                company_pin: base64Pin
            });

            if (res?.status === "success") {
                setOrders(res.data);
                setTotalPages(res.totalPages);
            }

        } catch (error) {
            toast.error("Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [page, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    // ✅ Confirm
    const handleConfirm = async (orderId) => {
        try {
            setLoading(true);
            await updateOrder(orderId, { is_confirmed: "confirmed" });
            toast.success("Order confirmed");
            await fetchOrders();
        } catch {
            toast.error("Failed to confirm order");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteStatus = async (orderId) => {
        try {
            setLoading(true);
            await updateOrder(selectedOrder.trackingId, { is_confirmed: "deleted", remark });
            toast.success("Order marked for deletion");
            setShowRejectModal(false);
            await fetchOrders();
        } catch {
            toast.error("Failed to mark order for deletion");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Reject
    const handleReject = (order) => {
        setSelectedOrder(order);
        setRemark(order.remark || "");
        setShowRejectModal(true);
    };

    const handleRejectSubmit = async () => {
        try {
            setLoading(true);
            await updateOrder(selectedOrder.trackingId, { is_confirmed: "rejected", remark });
            toast.success("Order rejected");
            setShowRejectModal(false);
            await fetchOrders();
        } catch {
            toast.error("Failed to reject order");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Edit
    const handleEdit = (order) => {
        setSelectedOrder(order);
        setEditData({ ...order });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async () => {
        try {
            setLoading(true);
            await updateOrder(selectedOrder.trackingId, editData);
            toast.success("Order updated");
            setShowEditModal(false);
            await fetchOrders();
        } catch {
            toast.error("Failed to update order");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (order) => {
        setSelectedOrder(order);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            await deleteOrder(selectedOrder?.trackingId);
            toast.success("Order deleted successfully");
            await fetchOrders();
            setShowDeleteModal(false);
        } catch {
            toast.error("Failed to delete order");
        } finally {
            setLoading(false);
        }
    };

    /* 🔹 CSV / EXCEL UPLOAD */
    const handleCSVUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        // 👇 yeh add karo
        formData.append("company_id", companyId);
        formData.append("company_pin", companyPin);

        const res = await addCSV(formData);

        if (res?.status === "success") {
            toast.success("CSV uploaded successfully");
            e.target.value = "";
        } else {
            toast.error(res?.message || "Upload failed");
        }
    };

    return (
        <div className="container pb-5">

            <div className="row">
                <div className="col">

                    <h4 className="mb-3 fw-semibold">Trashed Orders</h4>
                    <p>Orders deleted by users will appear here, Orders will be permanently removed automatically after a month.</p>
                </div>


            </div>

            {/* Filters */}
            <div className="row g-2 mb-3 justify-content-end align-items-end d-none">


                <div className="col-auto">

                    <ButtonGroup className='d-none'>
                        {["all", "confirmed", "rejected"].map(status => (
                            <ToggleButton
                                key={status}
                                type="radio"
                                className="text-capitalize"
                                variant={
                                    filters.is_confirmed === status
                                        ? "dark"
                                        : "outline-dark"
                                }
                                checked={filters.is_confirmed === status}
                                value={status}
                                onClick={() => {
                                    setFilters(prev => ({
                                        ...prev,
                                        is_confirmed: status
                                    }));
                                    setPage(1);
                                }}
                                disabled={loading}
                            >
                                {status}
                            </ToggleButton>
                        ))}
                    </ButtonGroup>
                    <div className="col-auto">
                        <Form.Label className="oms-label-dark">Status</Form.Label><br />

                        <div className="oms-filters">
                            {["all", "confirmed", "rejected"].map((status) => (
                                <div
                                    key={status}
                                    className={`oms-filter-chip ${filters.is_confirmed === status ? "active" : ""
                                        } ${loading ? "disabled" : ""}`}
                                    onClick={() => {
                                        if (loading) return;

                                        setFilters((prev) => ({
                                            ...prev,
                                            is_confirmed: status,
                                        }));

                                        setPage(1);
                                    }}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </div>
                            ))}
                        </div>
                    </div>


                </div>


                <div className="col-auto">
                    <Form.Label className='oms-label-dark'>From</Form.Label>
                    <Form.Control type="date" className="oms-input-dark" name="from" value={filters.from} onChange={handleFilterChange} />
                </div>

                <div className="col-auto">
                    <Form.Label className='oms-label-dark'>To</Form.Label>
                    <Form.Control type="date" className="oms-input-dark" name="to" value={filters.to} onChange={handleFilterChange} />
                </div>


                <div className="col-auto">
                    <Form.Label className='oms-label-dark'>Search</Form.Label>

                    <div className="oms-search">
                        <input
                            type="text"
                            name="trackingId"
                            placeholder="Search Order Id"
                            value={filters.trackingId}
                            onChange={handleFilterChange}
                            disabled={loading} />
                        <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
                            <FaSearch />
                        </span>
                    </div>

                </div>

            </div>

            {/* Table */}
            {/* <Table bordered responsive className='d-none'>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>VPP</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>City</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>

                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="11" className="text-center py-4">
                                <Spinner animation="border" variant="dark" />
                            </td>
                        </tr>
                    ) : orders.length === 0 ? (
                        <tr>
                            <td colSpan="11" className="text-center py-4">
                                No orders found
                            </td>
                        </tr>
                    ) : (
                        orders.map((order, idx) => (
                            <tr key={order.id} className={order.is_duplicate ? "table-danger" : ""}>
                                <td>{idx + 1 + (page - 1) * 50}</td>
                                <td>{order.trackingId}</td>
                                <td>{order.VppNumber || "-"}</td>
                                <td>{order.customerName}</td>
                                <td>{order.phoneNumber}</td>
                                <td>{order.city}</td>
                                <td>{order.product || "-"}</td>
                                <td>{order.quantity}</td>
                                <td>{order.amount}</td>
                                <td>
                                    <span className={`badge text-capitalize ${order.is_confirmed === "confirmed" ? "bg-success" : order.is_confirmed === "rejected" ? "bg-danger" : "bg-secondary"}`}>
                                        {order.is_confirmed || "pending"}
                                    </span>
                                </td>
                                <td>
                                    {order.is_confirmed === "rejected" ? (
                                        <span className="text-danger fw-semibold">
                                            {order.remark || "Rejected"}
                                        </span>
                                    ) : (
                                        <>
                                            <Button size="sm" variant="success" className="me-2" disabled={loading} onClick={() => handleConfirm(order.trackingId)}>
                                                <FaCheck />
                                            </Button>

                                            <Button size="sm" variant="danger" className="me-2" disabled={loading} onClick={() => handleReject(order)}>
                                                <IoCloseSharp />
                                            </Button>

                                            <Button size="sm" variant="primary" className="me-2" disabled={loading} onClick={() => handleEdit(order)}>
                                                <FaEdit />
                                            </Button>

                                            <Button size="sm" variant="dark" disabled={loading} onClick={() => handleDelete(order.trackingId)}>
                                                <FaTrash />
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table> */}

            <div className="oms-table-wrap">
                <div className="oms-table-header">
                    <div>
                        <span className="oms-table-title">Deleted Orders</span>
                        <span className="oms-table-count">{orders.length} orders</span>
                    </div>
                </div>
                {orders.length === 0 ? (
                    <div className="oms-empty">
                        <div className="oms-empty-icon">🔍</div>
                        <div className="oms-empty-text">No orders found</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table>
                            <thead>
                                <tr>

                                    <th>#</th>
                                    <th>Order Id</th>
                                    <th>Tracking</th>
                                    <th>Remark</th>

                                    {/* <th>Date</th> */}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders
                                    .filter(order => order.is_confirmed === "deleted")
                                    .map((order, index) => {
                                        const st = STATUS_CONFIG[order.is_confirmed] || STATUS_CONFIG.pending;

                                        return (
                                            <tr key={order.id}>

                                                {/* INDEX */}
                                                <td>{index + 1}</td>

                                                {/* TRACKING */}
                                                <td>
                                                    <div className="oms-track-id">{order.trackingId}</div>
                                                </td>

                                                {/* VPP */}
                                                <td>
                                                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                        {order.VppNumber || <span style={{ opacity: 0.4 }}>—</span>}
                                                    </div>
                                                </td>


                                                {/* STATUS BADGE (is_confirmed based) */}
                                                <td>
                                                    {order?.remark}
                                                </td>

                                                {/* STATUS CHANGE DROPDOWN */}
                                                <td>
                                                    <div className="oms-actions">

                                                        {order.is_confirmed !== "confirmed" && (
                                                            <>
                                                                <div className="oms-icon-btn print" title="Print Slip"
                                                                    onClick={() => handleConfirm(order.trackingId)}
                                                                >
                                                                    <IoReturnUpBackOutline size={10} />
                                                                    {/* 🖨 */}
                                                                </div>
                                                            </>
                                                        )}

                                                        <div className="oms-icon-btn cancel" title="Delete Order"
                                                            onClick={() => handleDelete(order)}
                                                        >
                                                            <FaTrash size={10} />
                                                        </div>


                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="oms-table-footer ">
                    <AppPagination page={page} totalPages={totalPages} onPageChange={setPage} />
                </div>
            </div>



            {/* Reject Modal */}
            {/* <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={remark}
                        onChange={e => setRemark(e.target.value)}
                        placeholder="Enter rejection reason"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleRejectSubmit} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" variant="light" /> : "Submit"}
                    </Button>
                </Modal.Footer>
            </Modal> */}

            {/* Edit Modal */}
            {/* <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Order: {selectedOrder?.trackingId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control name="customerName" value={editData.customerName || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control type="number" name="phoneNumber" value={editData.phoneNumber || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control name="address" value={editData.address || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Quantity</Form.Label>
                            <Form.Control type="number" name="quantity" value={editData.quantity || 1} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control type="number" name="amount" value={editData.amount || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>VPP Number</Form.Label>
                            <Form.Control maxLength={11} name="VppNumber" value={editData.VppNumber || ""} onChange={handleEditChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleEditSubmit} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" variant="light" /> : "Save Changes"}
                    </Button>
                </Modal.Footer>
            </Modal> */}


            <EditOrderModal
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                selectedOrder={selectedOrder}
                editData={editData}
                setEditData={setEditData}
                handleEditSubmit={handleEditSubmit}
                loading={loading} />

            <RejectOrderModal
                setAction={setAction}
                action={action}
                showRejectModal={showRejectModal}
                setShowRejectModal={setShowRejectModal}
                selectedOrder={selectedOrder}
                remark={remark}
                setRemark={setRemark}
                handleRejectSubmit={handleRejectSubmit}
                loading={loading}
                handleDeleteStatus={handleDeleteStatus}
            />


            <DeleteOrderModal
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                selectedOrder={selectedOrder}
                remark={remark}
                setRemark={setRemark}
                handleConfirmDelete={handleConfirmDelete}
                loading={loading}
            />
        </div>
    );
};

export default TrashedOrders;
